/**
 * Sets page - Browse all MTG sets with search and filtering
 */
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { type ReactElement, useCallback, useMemo, useState } from 'react';

import EmptyState from '@/components/EmptyState/EmptyState';
import Search from '@/components/Search/Search';
import Spinner from '@/components/Spinner/Spinner';
import { SET_TYPE_LABELS } from '@/lib/cardUtils';

import SetsTable from './components/SetsTable';
import { useSetsQuery } from './hooks/api';

// Important set types shown by default (most relevant for players)
const IMPORTANT_SET_TYPES = ['expansion', 'core', 'masters', 'draft_innovation', 'commander'] as const;

// Set types to show in filter, ordered by importance
const FILTER_SET_TYPES = [
  'expansion',
  'core',
  'masters',
  'draft_innovation',
  'commander',
  'starter',
  'duel_deck',
  'from_the_vault',
  'premium_deck',
  'spellbook',
  'masterpiece',
  'arsenal',
  'box',
  'promo',
  'token',
  'funny',
  'memorabilia',
  'alchemy',
] as const;

// Persisted filter state (stored as array since Set is not JSON-serializable)
const selectedSetTypesAtom = atomWithStorage<string[]>('mtgcraft:sets-selected-types', [...IMPORTANT_SET_TYPES]);
const showingAllSetsAtom = atomWithStorage<boolean>('mtgcraft:sets-show-all', false);
const searchQueryAtom = atomWithStorage<string>('mtgcraft:sets-search', '');

export default function SetsPage(): ReactElement {
  const { data: sets, isLoading, error } = useSetsQuery();
  const [searchQuery, setSearchQuery] = useAtom(searchQueryAtom);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [selectedTypesArray, setSelectedTypesArray] = useAtom(selectedSetTypesAtom);
  const [showingAllSets, setShowingAllSets] = useAtom(showingAllSetsAtom);
  const selectedTypes = useMemo(() => new Set(selectedTypesArray), [selectedTypesArray]);
  const setSelectedTypes = useCallback(
    (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      setSelectedTypesArray((prev) => {
        const prevSet = new Set(prev);
        const next = typeof updater === 'function' ? updater(prevSet) : updater;
        return Array.from(next);
      });
    },
    [setSelectedTypesArray],
  );

  const handleSearch = useCallback((query: string) => {
    setDebouncedSearchQuery(query);
  }, []);

  // Get available set types from data
  const availableTypes = useMemo(() => {
    if (!sets) return [];
    const types = new Set(sets.map((s) => s.set_type));
    return FILTER_SET_TYPES.filter((t) => types.has(t));
  }, [sets]);

  // Filter sets based on search and type filters
  const filteredSets = useMemo(() => {
    if (!sets) return [];

    return sets.filter((set) => {
      // Search filter (name or code)
      if (debouncedSearchQuery) {
        const query = debouncedSearchQuery.toLowerCase();
        const matchesName = set.name.toLowerCase().includes(query);
        const matchesCode = set.code.toLowerCase().includes(query);
        if (!matchesName && matchesCode === false) return false;
      }

      // Type filter (skip if showing all sets)
      if (!showingAllSets && selectedTypes.size > 0 && !selectedTypes.has(set.set_type)) {
        return false;
      }

      return true;
    });
  }, [sets, debouncedSearchQuery, selectedTypes, showingAllSets]);

  const toggleType = (type: string) => {
    // If toggling while showing all, switch to filtered mode
    if (showingAllSets) {
      setShowingAllSets(false);
      setSelectedTypes(new Set([type]));
      return;
    }

    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const toggleShowAllSets = () => {
    if (showingAllSets) {
      // Go back to important sets filter
      setShowingAllSets(false);
      setSelectedTypes(new Set(IMPORTANT_SET_TYPES));
    } else {
      // Show all sets
      setShowingAllSets(true);
      setSelectedTypes(new Set());
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTypes(new Set(IMPORTANT_SET_TYPES));
    setShowingAllSets(false);
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState title="Failed to load sets" description={error.message || 'An error occurred while loading sets data.'} />
      </div>
    );
  }

  const hasFilters = searchQuery || selectedTypes.size > 0 || showingAllSets;
  const isDefaultFilter =
    !searchQuery &&
    !showingAllSets &&
    selectedTypes.size === IMPORTANT_SET_TYPES.length &&
    IMPORTANT_SET_TYPES.every((t) => selectedTypes.has(t));

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Sets</h1>
          <p className="mt-0.5 text-sm text-zinc-500">
            {filteredSets.length} {filteredSets.length === 1 ? 'set' : 'sets'}
            {sets && filteredSets.length !== sets.length && ` of ${sets.length} total`}
          </p>
        </div>
        {!isDefaultFilter && (
          <button type="button" onClick={clearFilters} className="text-aether hover:text-aether/80 text-sm font-medium transition-colors">
            Reset filters
          </button>
        )}
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 border-b border-zinc-800/50 bg-zinc-900/30 px-6 py-3">
        {/* Search */}
        <div className="max-w-75 min-w-50 flex-1">
          <Search value={searchQuery} onSearch={handleSearch} onChangeImmediate={setSearchQuery} placeholder="Search by name or code..." />
        </div>

        {/* Type filter chips */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={toggleShowAllSets}
            className={clsx(
              'rounded-full px-3 py-1 text-xs font-medium transition-colors',
              showingAllSets ? 'bg-aether text-void' : 'bg-slate text-silver hover:bg-charcoal hover:text-pearl',
            )}
          >
            All Types
          </button>
          <span className="text-storm mx-1">|</span>
          {availableTypes.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => toggleType(type)}
              className={clsx(
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                !showingAllSets && selectedTypes.has(type)
                  ? 'bg-aether text-void'
                  : 'bg-slate text-silver hover:bg-charcoal hover:text-pearl',
              )}
            >
              {SET_TYPE_LABELS[type] || type}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        {filteredSets.length === 0 ? (
          <EmptyState title="No sets found" description={hasFilters ? 'Try adjusting your search or filters.' : 'No set data available.'} />
        ) : (
          <SetsTable sets={filteredSets} />
        )}
      </div>
    </div>
  );
}
